import React, { useEffect, useState } from 'react';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import { Provider } from 'react-redux';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { Icon } from 'semantic-ui-react';

import Api from './framework/api';
import loadBundle from './framework/loadBundle';
import Shell from './app/views/Shell';
import Logout from './user/views/Logout';
import { setAuthToken } from './framework/localStorage';

import AuthLoginNew from './user/views/AuthLoginNew';

import Unauthorized from './user/views/Unauthorized';
import TermsOfService from './user/views/TermsOfService';
import GoogleLoading from '../apps/network/views/GoogleLoading';
import { ProtectedRoute } from './app/views';

const AppRoutes = ({ store, bundles: { Apps, Resources, Analytics, Settings } }) => {
  const { isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const isLogout = useRouteMatch({ path: '/logout', exact: true });
  const [silentLoginTried, setSilentLoginTried] = useState(false);

  // Pages mount only after the SDK settles, so this is set before their first fetch
  useEffect(() => {
    Api.setTokenProvider(async () => {
      const token = await getAccessTokenSilently();
      setAuthToken(token);
      return token;
    });
  }, [getAccessTokenSilently]);

  // The SDK only renews a session it remembers from its own hint cookie, so a session started in
  // another Chartboost dashboard needs one explicit try. On success the SDK flips isAuthenticated.
  useEffect(() => {
    if (isLoading || isAuthenticated || isLogout || silentLoginTried) {
      return;
    }
    getAccessTokenSilently()
      .catch(() => {})
      .finally(() => setSilentLoginTried(true));
  }, [isLoading, isAuthenticated]);

  // Sign out before the SDK settles: waiting would first renew the session with a silent login.
  if (isLogout) {
    return <Logout />;
  }

  if (isLoading || (!isAuthenticated && !silentLoginTried)) {
    // Not semantic's Loader: credentials.css pushes every .ui.loader 50vh down
    return (
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}
      >
        <Icon name="spinner" loading size="big" />
      </div>
    );
  }

  return (
    <Switch>
      <Route exact path="/login">
        <AuthLoginNew />
      </Route>

      {!isAuthenticated && (
        <Route exact path="/apps">
          <Redirect to="/login" />
        </Route>
      )}
      <Route exact path="/unauthorized">
        <Unauthorized />
      </Route>
      <Route exact path="/terms-of-service">
        <TermsOfService />
      </Route>
      {!isAuthenticated && (
        <Route>
          {/* When not logged in, redirect to login */}
          <Redirect to="/login" />
        </Route>
      )}
      <Shell>
        <Switch>
          <Route exact path="/">
            <Redirect to="/login" />
          </Route>
          <Route path="/google_code" component={GoogleLoading} />
          <ProtectedRoute component={Apps(store)} name="apps" path="/apps" />
          <ProtectedRoute component={Resources(store)} name="resources" path="/resources" />
          <ProtectedRoute component={Analytics(store)} name="analytics" path="/analytics" />
          <ProtectedRoute component={Settings(store)} name="settings" path="/settings" />
          <Route>
            <Redirect to="/apps" />
          </Route>
        </Switch>
      </Shell>
    </Switch>
  );
};

const CoreRoutes = ({ store }) => {
  const bundles = {
    Apps: loadBundle(() => import('apps'), store),
    Resources: loadBundle(() => import('resources'), store),
    Analytics: loadBundle(() => import('analytics'), store),
    Settings: loadBundle(() => import('settings'), store),
  };

  const auth0_domain = process.env.REACT_APP_AUTH0_DOMAIN;
  const auth0_client_id = process.env.REACT_APP_AUTH0_CLIENT_ID;
  const auth0_audience = process.env.REACT_APP_AUTH0_AUDIENCE;

  return (
    <Auth0Provider
      domain={auth0_domain || 'chartboost-int.us.auth0.com'}
      clientId={auth0_client_id || 'hgma8MP8RdBxEwyJlCYSVbW1PbsiuY9O'}
      authorizationParams={{
        redirect_uri: `${window.location.origin}/login`,
        audience: auth0_audience || 'https://api.gateway.chartboost.com',
      }}
    >
      <Provider store={store}>
        <AppRoutes store={store} bundles={bundles} />
      </Provider>
    </Auth0Provider>
  );
};

export default CoreRoutes;
